import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { ErrorBox, Field, Loading, RupeeInput, inr, rupeesToPaise, useBusy } from '../../ui';
import { formatINR } from '@shared/money';

interface PolicyResp {
  policy: {
    version: number;
    maxPerTransactionPaise?: string;
    personDailyCapPaise?: string;
    personMonthlyCapPaise?: string;
    vehicleDailyCapPaise?: string;
    vehicleMonthlyCapPaise?: string;
    requireOdometer: boolean;
    requireDriverAck?: boolean;
  } | null;
  approvers: { primary?: string; backup?: string };
}

const FIELDS: [keyof NonNullable<PolicyResp['policy']>, string][] = [
  ['maxPerTransactionPaise', 'Most for one purchase'],
  ['personDailyCapPaise', 'Each driver per day'],
  ['personMonthlyCapPaise', 'Each driver per month'],
  ['vehicleDailyCapPaise', 'Each vehicle per day'],
  ['vehicleMonthlyCapPaise', 'Each vehicle per month'],
];

const toRupees = (p?: string) => (p ? formatINR(p, { symbol: false }).replace(/,/g, '') : '');

export function Policy() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['policy'], queryFn: () => api.get<PolicyResp>('/api/policy') });
  const members = useQuery({ queryKey: ['members'], queryFn: () => api.get<{ userId: string; name?: string; roles: string[]; status: string }[]>('/api/org/members') });
  const [vals, setVals] = useState<Record<string, string>>({});
  const [odo, setOdo] = useState(false);
  const [ack, setAck] = useState(false);
  const [primary, setPrimary] = useState('');
  const [backup, setBackup] = useState('');
  const [saved, setSaved] = useState('');
  const { busy, error, run } = useBusy();
  useEffect(() => {
    if (!q.data?.policy) return;
    setVals(Object.fromEntries(FIELDS.map(([k]) => [k, toRupees(q.data!.policy![k] as string | undefined)])));
    setOdo(q.data.policy.requireOdometer);
    setAck(Boolean(q.data.policy.requireDriverAck));
    setPrimary(q.data.approvers.primary ?? '');
    setBackup(q.data.approvers.backup ?? '');
  }, [q.data]);
  if (q.isLoading) return <Loading />;
  const approvers = members.data?.filter((m) => m.status === 'ACTIVE' && m.roles.some((r) => r === 'BUYER_APPROVER' || r === 'BUYER_ADMIN')) ?? [];
  return (
    <div className="stack">
      <div className="card">
        <h1>Rules and limits</h1>
        <p className="muted">These are your company’s own spending rules. They are checked when an indent is approved, together with your credit with the supplier. Paying a bill does not reset a day or month limit.</p>
        <ErrorBox error={q.error} />
        <div className="grid two">
          {FIELDS.map(([k, label]) => (
            <Field key={k} label={`${label} (₹)`} hint={q.data?.policy?.[k] ? `Now ${inr(q.data.policy[k] as string)}` : 'No limit'}>
              <RupeeInput value={vals[k] ?? ''} onChange={(v) => setVals({ ...vals, [k]: v })} placeholder="No limit" />
            </Field>
          ))}
        </div>
        <label className="check" style={{ marginTop: '1rem' }}><input type="checkbox" checked={odo} onChange={(e) => setOdo(e.target.checked)} /> Odometer reading is required at every fill</label>
        <label className="check"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> Driver confirms each fill with an SMS code (saved drivers only). Off: the scanned QR and the pump’s bill are the record.</label>
        <ErrorBox error={error} />
        {saved ? <div className="alert ok">{saved}</div> : null}
        <button className="btn primary" disabled={busy} onClick={() => void run(async () => {
          const body: Record<string, unknown> = { requireOdometer: odo, requireDriverAck: ack };
          for (const [k] of FIELDS) body[k] = vals[k]?.trim() ? rupeesToPaise(vals[k]) : null;
          await api.put('/api/policy', body);
          await qc.invalidateQueries({ queryKey: ['policy'] });
          setSaved('Rules saved.');
        })}>Save rules</button>
      </div>
      <div className="card">
        <h2>Who approves indents</h2>
        <p className="muted">The main approver gets each request first; the backup is told too.</p>
        <div className="grid two">
          <Field label="Main approver"><select value={primary} onChange={(e) => setPrimary(e.target.value)}><option value="">—</option>{approvers.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}</select></Field>
          <Field label="Backup approver"><select value={backup} onChange={(e) => setBackup(e.target.value)}><option value="">—</option>{approvers.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}</select></Field>
        </div>
        <button className="btn" disabled={busy} onClick={() => void run(async () => { await api.put('/api/org/approvers', { primaryUserId: primary || undefined, backupUserId: backup || undefined }); setSaved('Approvers saved.'); })}>Save approvers</button>
      </div>
      <div className="card">
        <h2>Standing approvals</h2>
        <p className="muted">Not available in this version. Every purchase needs an approved indent.</p>
      </div>
    </div>
  );
}
